'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '#about' },
  {
    label: 'Services',
    href: '/services',
    children: [
      { label: 'Web Design', href: '/services/web-design' },
      { label: 'Web Development', href: '/services/web-development' },
      { label: 'E-Commerce', href: '/services/e-commerce' },
      { label: 'SEO & Performance', href: '/services/seo-performance' },
      { label: 'UI/UX Design', href: '/services/ui-ux-design' },
      { label: 'Mobile Apps', href: '/services/mobile-apps' },
    ],
  },
  { label: 'Portfolio', href: '/projects' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Blog', href: '/blog' },
  { label: 'Contact', href: '#contact' },
];

export default function PublicNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'glass-card border-b border-border/50 py-3' :'bg-transparent py-5'
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 group">
            <AppLogo size={36} />
            <span className="font-display font-bold text-xl tracking-tight text-foreground group-hover:text-gradient-primary transition-all duration-300">
              WebMint
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks?.map((link) =>
              link?.children ? (
                <div key={`nav-${link?.label}`} className="relative">
                  <button
                    className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-200"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    {link?.label}
                    <ChevronDown size={14} className={`transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {servicesOpen && (
                    <div
                      className="absolute top-full left-0 mt-2 w-52 glass-card rounded-xl p-2 shadow-modal border border-border/60"
                      onMouseEnter={() => setServicesOpen(true)}
                      onMouseLeave={() => setServicesOpen(false)}
                    >
                      {link?.children?.map((child) => (
                        <Link
                          key={`nav-child-${child?.label}`}
                          href={child?.href}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-primary/10 transition-all duration-150"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-accent/60" />
                          {child?.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={`nav-${link?.label}`}
                  href={link?.href}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-200"
                >
                  {link?.label}
                </Link>
              )
            )}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              href="/login"
              className="px-4 py-2 rounded-lg text-sm font-semibold font-display text-muted-foreground hover:text-foreground border border-border/60 hover:border-primary/50 transition-all duration-200"
            >
              Admin Login
            </Link>
            <Link
              href="/get-website"
              className="btn-primary text-sm"
            >
              Get Your Website
              <ArrowRight size={14} />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-200"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-border/40 pt-4 space-y-1">
            {navLinks?.map((link) => (
              <Link
                key={`mobile-nav-${link?.label}`}
                href={link?.href}
                className="block px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-200"
                onClick={() => setMobileOpen(false)}
              >
                {link?.label}
              </Link>
            ))}
            <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-border/40">
              <Link
                href="/login"
                className="block text-center px-4 py-3 rounded-lg text-sm font-semibold border border-border/60 text-muted-foreground hover:text-foreground transition-all duration-200"
                onClick={() => setMobileOpen(false)}
              >
                Admin Login
              </Link>
              <Link
                href="/get-website"
                className="btn-primary text-sm justify-center"
                onClick={() => setMobileOpen(false)}
              >
                Get Your Website <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}